import type { Configuration, Node, TOCConfig } from '../types';
import { Logger } from './Logger.js';

/**
 * Heading information extracted from AST
 */
export interface HeadingInfo {
  id: string;
  text: string;
  level: number;
  children?: HeadingInfo[];
}

/**
 * TOC generation options
 */
export interface TOCOptions {
  depth: number;
  title: string;
  position: string;
  className?: string;
  includeLinks?: boolean;
}

/**
 * Table of Contents generator for Markdown documents
 *
 * Features:
 * - Extracts headings from AST with configurable depth
 * - Generates hierarchical TOC structure
 * - Creates HTML with proper semantic markup
 * - Supports custom positioning and styling
 *
 * @example
 * ```typescript
 * const generator = new TOCGenerator(config);
 * const tocHtml = generator.generate(ast);
 * ```
 */
export class TOCGenerator {
  private logger: Logger;
  private tocConfig: TOCConfig;

  constructor(config: Configuration) {
    this.config = config;
    this.logger = new Logger(config.logging);
    this.tocConfig = config.features.toc;

    this.logger.debug('TOCGenerator initialized', {
      enabled: this.tocConfig.enabled,
      depth: this.tocConfig.depth,
      title: this.tocConfig.title,
      position: this.tocConfig.position,
    });
  }

  /**
   * Generate TOC HTML from AST
   * @param ast - Parsed markdown AST
   * @returns HTML string for TOC
   */
  generate(ast: Node): string {
    if (!this.tocConfig.enabled) {
      this.logger.debug('TOC generation disabled');
      return '';
    }

    const startTime = Date.now();
    this.logger.trace('Starting TOC generation');

    try {
      // Extract headings from AST
      const headings = this.extractHeadings(ast);

      if (headings.length === 0) {
        this.logger.info('No headings found for TOC generation');
        return '';
      }

      // Filter headings by depth
      const filteredHeadings = this.filterHeadingsByDepth(headings, this.tocConfig.depth);

      if (filteredHeadings.length === 0) {
        this.logger.info('No headings within configured depth for TOC');
        return '';
      }

      // Build hierarchical structure
      const hierarchicalHeadings = this.buildHierarchy(filteredHeadings);

      // Generate HTML
      const tocHtml = this.generateHTML(hierarchicalHeadings);

      const processingTime = Date.now() - startTime;
      this.logger.info('TOC generation completed', {
        headingCount: filteredHeadings.length,
        processingTimeMs: processingTime,
      });

      return tocHtml;
    } catch (error) {
      this.logger.error('TOC generation failed', error as Error);
      return '';
    }
  }

  /**
   * Extract heading information from AST
   * @param ast - AST node to traverse
   * @param headings - Accumulator for headings
   * @returns Array of heading information
   */
  private extractHeadings(ast: Node, headings: HeadingInfo[] = []): HeadingInfo[] {
    // Check if current node is a heading using improved type checking
    if (this.isValidHeadingNode(ast)) {
      const level = (ast as any).depth || 1;
      const text = this.extractTextFromNode(ast);
      const hProps = ast.data?.hProperties as Record<string, any>;
      const id = hProps?.id as string;

      if (text && id && typeof id === 'string') {
        headings.push({
          id,
          text: text.trim(),
          level,
        });
      }
    }

    // Recursively traverse children
    if (ast.children) {
      for (const child of ast.children) {
        this.extractHeadings(child, headings);
      }
    }

    return headings;
  }

  /**
   * Type-safe check if node is a valid heading node
   * @param node - AST node to check
   * @returns True if node is valid heading with required properties
   */
  private isValidHeadingNode(node: Node): boolean {
    return (
      node.type === 'heading' &&
      'depth' in node &&
      typeof (node as any).depth === 'number' &&
      node.data != null &&
      typeof node.data === 'object' &&
      'hProperties' in node.data &&
      node.data.hProperties != null &&
      typeof node.data.hProperties === 'object' &&
      'id' in node.data.hProperties &&
      typeof (node.data.hProperties as Record<string, any>).id === 'string'
    );
  }

  /**
   * Extract text content from heading node
   * @param node - Heading AST node
   * @returns Extracted text content
   */
  private extractTextFromNode(node: Node): string {
    if (node.type === 'text' && node.value) {
      return node.value;
    }

    if (node.children) {
      return node.children.map((child) => this.extractTextFromNode(child)).join('');
    }

    return '';
  }

  /**
   * Filter headings by maximum depth
   * @param headings - All extracted headings
   * @param maxDepth - Maximum heading level to include
   * @returns Filtered headings array
   */
  private filterHeadingsByDepth(headings: HeadingInfo[], maxDepth: number): HeadingInfo[] {
    return headings.filter((heading) => heading.level <= maxDepth);
  }

  /**
   * Build hierarchical structure from flat heading list
   * @param headings - Flat array of headings
   * @returns Hierarchical heading structure
   */
  private buildHierarchy(headings: HeadingInfo[]): HeadingInfo[] {
    const result: HeadingInfo[] = [];
    const stack: HeadingInfo[] = [];

    for (const heading of headings) {
      // Remove headings from stack that are at same or deeper level
      while (stack.length > 0) {
        const lastItem = stack[stack.length - 1];
        if (lastItem && lastItem.level >= heading.level) {
          stack.pop();
        } else {
          break;
        }
      }

      // If stack is empty, this is a top-level heading
      if (stack.length === 0) {
        result.push(heading);
      } else {
        // Add as child to the parent heading
        const parent = stack[stack.length - 1];
        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(heading);
        }
      }

      stack.push(heading);
    }

    return result;
  }

  /**
   * Generate HTML from hierarchical heading structure
   * @param headings - Hierarchical heading structure
   * @returns HTML string
   */
  private generateHTML(headings: HeadingInfo[]): string {
    if (headings.length === 0) {
      return '';
    }

    const tocId = 'table-of-contents';
    const tocClass = 'toc';

    let html = `<nav id="${tocId}" class="${tocClass}" role="navigation" aria-labelledby="toc-title">`;
    html += `<h2 id="toc-title" class="toc-title">${this.escapeHtml(this.tocConfig.title)}</h2>`;
    html += this.generateListHTML(headings, 1);
    html += '</nav>';

    return html;
  }

  /**
   * Generate nested list HTML for headings
   * @param headings - Heading structure at current level
   * @param depth - Current nesting depth
   * @returns HTML string for list
   */
  private generateListHTML(headings: HeadingInfo[], depth: number): string {
    if (headings.length === 0) {
      return '';
    }

    const className = `toc-list toc-level-${depth}`;
    let html = `<ol class="${className}">`;

    for (const heading of headings) {
      html += '<li class="toc-item">';
      html += `<a href="#${this.escapeHtml(heading.id)}" class="toc-link">`;
      html += this.escapeHtml(heading.text);
      html += '</a>';

      // Add nested children if they exist
      if (heading.children && heading.children.length > 0) {
        html += this.generateListHTML(heading.children, depth + 1);
      }

      html += '</li>';
    }

    html += '</ol>';
    return html;
  }

  /**
   * Escape HTML characters
   * @param text - Text to escape
   * @returns Escaped HTML
   */
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Check if TOC should be generated
   * @returns Whether TOC is enabled
   */
  isEnabled(): boolean {
    return this.tocConfig.enabled;
  }

  /**
   * Get TOC configuration
   * @returns TOC configuration object
   */
  getConfig(): TOCConfig {
    return { ...this.tocConfig };
  }

  /**
   * Get CSS file path for TOC styling
   * @returns Path to TOC CSS file
   */
  getTOCStylesPath(): string {
    return '../styles/toc.css';
  }

  /**
   * Load TOC CSS content from external file
   * @returns Promise resolving to CSS string for TOC styling
   */
  async getTOCStyles(): Promise<string> {
    try {
      const fs = await import('node:fs/promises');
      const path = await import('node:path');

      const cssPath = path.resolve(__dirname, '../styles/toc.css');
      const cssContent = await fs.readFile(cssPath, 'utf-8');

      this.logger.debug('TOC styles loaded from external CSS file');
      return cssContent;
    } catch (error) {
      this.logger.warn('Failed to load external TOC CSS, using fallback', error as Error);

      // Fallback minimal CSS if external file fails
      return `
.toc {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1.5rem;
  margin: 2rem 0;
  page-break-inside: avoid;
}

.toc-title {
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  padding-bottom: 0.5rem;
}

.toc-list {
  margin: 0;
  padding-left: 1.5rem;
  list-style: none;
}

.toc-link {
  color: #495057;
  text-decoration: none;
}

.toc-link:hover {
  color: #007bff;
}
      `.trim();
    }
  }
}
